import ClickLogger from '@/components/common/ClickLogger';
import LinkTag from '@/components/common/LinkTag';
import { ENCYCLOPEDIA_LOG_EVENT } from '@/constants/logs/encyclopedia';
import ContentMarkDown from '@/shared/ui/content-mark-down/ContentMarkDown';
import { Tablet } from '@/styles/layout';
import styled from 'styled-components';

interface Props {
  content: string;
  onClickOuterLink: (url: string) => void;
  extractTextFromChildren: (children: React.ReactNode) => string;
  encyclopediaPid: string;
}

const DiseaseEncyclopediaMarkDown = ({
  content,
  onClickOuterLink,
  extractTextFromChildren,
  encyclopediaPid,
}: Props) => {
  return (
    <Wrapper className='mb-[40px] px-[24px]'>
      <ContentMarkDown
        markdown={content}
        components={{
          h2: (node, ...rest) => {
            return (
              <h2 id={`${node.children}`} {...rest}>
                {node.children}
              </h2>
            );
          },
          h3: (node, ...rest) => {
            return (
              <h3 id={`${node.children}`} {...rest}>
                {node.children}
              </h3>
            );
          },
          h4: (node, ...rest) => {
            return (
              <h4 id={`${node.children}`} {...rest}>
                {node.children}
              </h4>
            );
          },
          div: (node, ...rest) => {
            return (
              <div className={node.className} {...rest}>
                {node.children}
              </div>
            );
          },
          p: (node, ...rest) => {
            if (typeof node.children[0] === 'object') {
              const element: any = node.children[0];
              if (element.type === 'img') {
                return <figure>{node.children}</figure>;
              }
            }
            return <p {...rest}>{node.children}</p>;
          },
          a: (node) => {
            return (
              <ClickLogger
                params={{
                  eventName: ENCYCLOPEDIA_LOG_EVENT.CLICK_OUTER_LINK,
                  payload: {
                    encyclopediaPid: encyclopediaPid ?? '',
                    linkUrl: node.href ?? '',
                    linkText: extractTextFromChildren(node.children),
                  },
                }}
              >
                <LinkTag
                  href={node.href ?? ''}
                  onClickNavigateToScreen={() => onClickOuterLink(node.href ?? '')}
                  target='_blank'
                  className='encyclopedia-link'
                  wrapperStyle={{
                    display: 'inline-block',
                  }}
                >
                  {node.children}
                </LinkTag>
              </ClickLogger>
            );
          },
        }}
      />
    </Wrapper>
  );
};
export default DiseaseEncyclopediaMarkDown;

export const Wrapper = styled.div`
  width: 100%;

  iframe {
    margin-bottom: 40px;

    @media screen and (max-width: ${Tablet}) {
      margin-bottom: 28px;
      width: 100%;
    }
  }

  p,
  figure {
    ${({ theme }) => theme.typography.ARTICLE18R};
    color: ${({ theme }) => theme.color.gray600};
    margin-bottom: 16px;
  }

  h1 {
    margin-bottom: 16px;

    @media screen and (max-width: ${Tablet}) {
      margin-bottom: 12px;
    }
  }

  h2 {
    width: 100%;
    ${({ theme }) => theme.typography.UI24SB};
    color: ${({ theme }) => theme.color.gray800};
    margin-top: 56px;
    margin-bottom: 16px;

    @media screen and (max-width: ${Tablet}) {
      ${({ theme }) => theme.typography.UI20SB};
      margin-bottom: 12px;
    }
  }

  h3 {
    width: 100%;
    ${({ theme }) => theme.typography.UI20SB};
    color: ${({ theme }) => theme.color.gray800};
    margin-top: 16px;
    margin-bottom: 16px;

    @media screen and (max-width: ${Tablet}) {
      ${({ theme }) => theme.typography.UI18SB};
      margin-bottom: 12px;
    }
  }

  img {
    max-width: 736px;
    width: 100%;
    height: auto;
    position: relative;
    border-radius: 12px;
    margin-bottom: 24px;
  }

  ul {
    ${({ theme }) => theme.typography.UI18R};
    color: ${({ theme }) => theme.color.gray600};
    margin-bottom: 16px;

    li {
      position: relative;
      padding-left: 24px;
      cursor: inherit;

      &:before {
        content: '';
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: ${({ theme }) => theme.color.gray600};
        display: block;
        position: absolute;
        left: 10px;
        top: 10px;
      }
    }

    @media screen and (max-width: ${Tablet}) {
      ${({ theme }) => theme.typography.UI16R};
      margin-bottom: 28px;
    }
  }

  ol {
    p {
      white-space: pre-wrap;
    }

    ${({ theme }) => theme.typography.UI18R};
    color: ${({ theme }) => theme.color.gray600};
    margin: 0 0 28px 0;
    padding-inline-start: 28px;

    @media screen and (max-width: ${Tablet}) {
      ${({ theme }) => theme.typography.UI16R};
    }
    list-style: auto;
  }

  li {
    word-break: break-word;

    &:not(:last-of-type) {
      margin-bottom: 12px;
    }

    ul,
    ol {
      margin-top: 16px;
      margin-bottom: 0;

      li {
        &:before {
          content: '';
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: ${({ theme }) => theme.color.white};
          border: 1px solid ${({ theme }) => theme.color.gray600};
          display: block;
          position: absolute;
          left: 10px;
          top: 10px;
        }
      }
    }
  }

  a, .encyclopedia-link {
    color: ${({ theme }) => theme.color.blue500};

    &:hover {
      text-decoration: underline;
    }
  }


  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 16px;
    border: 1px solid ${({ theme }) => theme.color.gray100};

    thead {
      ${({ theme }) => theme.typography.UI16SB};
      color: ${({ theme }) => theme.color.gray700};
      background-color: ${({ theme }) => theme.color.gray30};

      th {
        padding: 16px;
        border: 1px solid ${({ theme }) => theme.color.gray100};
      }
    }

    tbody {
      ${({ theme }) => theme.typography.UI16R};
      color: ${({ theme }) => theme.color.gray500};

      td {
        border: 1px solid ${({ theme }) => theme.color.gray100};
        padding: 16px;
        text-align: center;
        vertical-align: middle;
      }
    }
  }
`;
